import classnames from 'classnames'
import * as React from 'react'

import { ChartConfigChartPane, CustomSpineEvents } from '../../../../types/shared'
import { Button, ButtonGroup, Select, SelectOption } from '../../../components'
import { IndicatorType } from '../../../constants/common'
import ChartLayout from '../../../models/chart_layout'
import utils from '../../../utils'
import { handleAddIndicatorPane, handleRemoveIndicatorPane } from './model-manipulation'

interface Props {
  chartLayoutModel: ChartLayout
  onUpdate: () => void
}
export function CotSelect({ chartLayoutModel, onUpdate }: Props) {
  const handleCotsOff = React.useCallback(() => {
    // handleRemoveIndicatorPane uses paneIndex and since we may remove multiple panes at once we need to get correct index after each removal
    const removeCotPanesRecursively = () => {
      const paneIndex = chartLayoutModel
        .charts()
        .first()
        .getAllPanes()
        .findIndex((pane) => pane.mainElement()?.instance.type === IndicatorType.Cot)

      if (paneIndex > -1) {
        handleRemoveIndicatorPane({
          chartLayoutModel,
          paneIndex,
        })
        removeCotPanesRecursively()
      }
    }
    removeCotPanesRecursively()
    onUpdate()
  }, [chartLayoutModel, onUpdate])

  const handleCotsOn = React.useCallback(() => {
    const cotKeys = Object.keys(chartLayoutModel.charts().first().quote().COTs ?? {})
    if (cotKeys.length === 0) {
      cotKeys.push('')
    }

    cotKeys.forEach((code) => {
      handleAddIndicatorPane({
        chartLayoutModel,
        indicatorElements: [{ type: IndicatorType.Cot, code }],
      })
    })

    chartLayoutModel.getAllCharts().forEach((chart) => {
      const chartPanes = [...chart.getAllPanes()].sort((a, b) => a.getNumericId() - b.getNumericId())
      const minCotPaneId = utils.min(
        chartPanes
          .filter((pane) => pane.mainElement()?.instance.type === IndicatorType.Cot)
          .map((pane) => pane.getNumericId())
      )
      const chartPaneNumericId = chartPanes.find((pane) => pane.getIsChartPane())!.getNumericId()
      chartPanes.forEach((pane) => {
        if (pane.getNumericId() < minCotPaneId && chartPaneNumericId < pane.getNumericId()) {
          const paneConfig = pane.toConfig()
          pane.destroyCascade()
          chart.createPaneCascade({
            ...(paneConfig as ChartConfigChartPane),
            chart,
          })
        }
      })

      const chartHeight = chart.getAllPanes().reduce((previous, pane) => {
        pane.resetHeight()
        return previous + pane.height
      }, 0)
      chart.updateAttribute('height', chartHeight)
      chart.trigger(CustomSpineEvents.IndicatorsChange)
    })

    onUpdate()
  }, [chartLayoutModel, onUpdate])

  const hasCot = chartLayoutModel.getAllElements().some(({ instance }) => instance.type === IndicatorType.Cot)

  return (
    <div className="mb-2 space-y-6 xs:space-y-2">
      <div className={classnames('grid gap-1 xs:grid-cols-[0.6fr_1fr]')}>
        <Select
          aria-label="COTs"
          labelClassName="overflow-hidden -m-0.5 p-0.5"
          placeholder="COTs"
          size="medium"
          disabled
        >
          <SelectOption>COTs</SelectOption>
        </Select>

        <div className="grid grid-cols-[1fr_2rem] gap-1">
          <div className="grid flex-1 grid-cols-[repeat(3,1fr)] gap-1">
            <div className="flex items-center" style={{ gridColumn: `span ${2}` }}>
              <div className="h-px w-full bg-gray-200 dark:bg-gray-700" />
            </div>
            <ButtonGroup className="flex w-full flex-shrink-0 p-0.5" hasDivider={false}>
              <Button
                className="h-full grow"
                contentClass="flex justify-center"
                rounding="small"
                size="none"
                type="button"
                theme={!hasCot ? 'blue' : undefined}
                onClick={handleCotsOff}
              >
                Off
              </Button>
              <Button
                className="h-full grow"
                contentClass="flex justify-center"
                rounding="small"
                size="none"
                type="button"
                theme={hasCot ? 'blue' : undefined}
                onClick={hasCot ? undefined : handleCotsOn}
              >
                On
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </div>
  )
}
